import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@/lib/theme'
import { OrgProvider, useOrg } from '@/lib/orgs'
import { AuthProvider } from '@/lib/auth/context'
import { LiveKitProvider, useLiveKit } from '@/lib/livekit'
import { useMeetingStore } from '@/hooks'
import { Sidebar } from '@/components/Sidebar'
import { DashboardGlobal } from '@/components/DashboardGlobal'
import { DashboardCompany } from '@/components/DashboardCompany'
import { LoginPage } from '@/components/auth/LoginPage'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { ProjectsView, PublicAvailabilityView } from '@/components/views'
import { WebmailView } from '@/components/webmail'
import { MeetingRoomsView, MeetingMiniView, GuestMeetingPage } from '@/components/meetings'
import { CalendarView } from '@/components/views/CalendarView'
import { PublicBookingPage } from '@/components/booking'
import { AnimatorApplicationPage } from '@/components/apply'

function DashboardLayout() {
  const { currentOrg, currentTab, setCurrentOrg, setCurrentTab, isGlobalView, organizations, isLoading, selectedProjectId, setSelectedProjectId } = useOrg()
  const { isConnected } = useLiveKit()
  const { roomId } = useMeetingStore()

  return (
    <div className="min-h-screen text-[var(--text-main)] font-sans selection:bg-[var(--primary)] selection:text-[var(--bg-main)] bg-[var(--bg-main)]">
      <Sidebar
        currentOrg={currentOrg}
        currentTab={currentTab}
        onOrgChange={setCurrentOrg}
        onTabChange={setCurrentTab}
        organizations={organizations}
        isLoading={isLoading}
        selectedProjectId={selectedProjectId}
        onProjectChange={setSelectedProjectId}
        isInMeeting={isConnected}
        meetingRoomId={roomId}
      />

      {/* Floating mini-view when in a meeting but on a different tab */}
      <MeetingMiniView
        currentTab={currentTab}
        onReturnToMeeting={() => {
          if (currentOrg !== 'global') {
            setCurrentOrg('global')
          }
          setCurrentTab('meetings')
        }}
      />

      <main className="ml-64 p-8 pb-16 transition-colors duration-300">
        {/* ProjectsView, WebmailView, MeetingRoomsView, and CalendarView use full width and are always global, other views use max-w-7xl */}
        {currentTab === 'calendar' ? (
          <div className="animate-fade-in w-full h-[calc(100vh-4rem)]">
            <CalendarView />
          </div>
        ) : currentTab === 'availability' ? (
          <div className="animate-fade-in w-full h-[calc(100vh-4rem)]">
            <PublicAvailabilityView />
          </div>
        ) : currentTab === 'meetings' ? (
          <div className="animate-fade-in w-full h-[calc(100vh-4rem)]">
            <MeetingRoomsView />
          </div>
        ) : currentTab === 'webmail' ? (
          <div className="animate-fade-in w-full h-[calc(100vh-4rem)]">
            <WebmailView />
          </div>
        ) : currentTab === 'projects' ? (
          <div className="animate-fade-in w-full">
            <ProjectsView selectedProjectId={selectedProjectId} onProjectChange={setSelectedProjectId} />
          </div>
        ) : (
          <div className="max-w-7xl mx-auto animate-fade-in w-full">
            {isGlobalView ? (
              <DashboardGlobal onSelectOrg={setCurrentOrg} />
            ) : (
              <DashboardCompany />
            )}
          </div>
        )}
      </main>
    </div>
  )
}

function AppRoutes() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/apply" element={<AnimatorApplicationPage />} />
        <Route path="/meeting/:roomId" element={<GuestMeetingPage />} />
        <Route
          path="/*"
          element={
            <AuthGuard>
              <OrgProvider>
                <LiveKitProvider>
                  <DashboardLayout />
                </LiveKitProvider>
              </OrgProvider>
            </AuthGuard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AppRoutes />
    </ThemeProvider>
  )
}

export default App
